import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  type RefObject,
} from "react";

// Interactive widgets that should become the focus target for their cell
// instead of the cell itself (WAI-ARIA grid: a cell with a single widget puts
// the roving tabindex on the widget so it can be operated directly).
const WIDGET_SELECTOR =
  "button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled])";

interface UseGridCellNavigationOptions<T extends HTMLElement = HTMLElement> {
  /** Ref to an element that contains the `[role="grid"]` table. */
  containerRef: RefObject<T | null>;
  /** When false the hook is inert — no listeners, no tabindex management. */
  enabled: boolean;
  /** Called on Escape while focus is inside the grid (e.g. to dismiss detail). */
  onEscape?: () => void;
}

/**
 * Implements the WAI-ARIA data grid keyboard model (APG "Data Grids" example):
 * cell-level roving tabindex so the whole grid is a single tab stop, 2D arrow
 * navigation, Home/End within a row, and Ctrl+Home/End to the grid corners.
 *
 * Focus targets are resolved per cell: a cell containing a single interactive
 * widget hands the roving tabindex to that widget (so Enter/Space operate it
 * directly); otherwise the cell itself becomes focusable. Initial focus lands
 * on the first DATA row's first cell, not the header.
 *
 * The tabindex is managed imperatively against the live DOM so it survives the
 * row re-renders that pagination/sorting cause. The hook never moves focus on
 * its own — only in response to a navigation keypress.
 */
function useGridCellNavigation<T extends HTMLElement = HTMLElement>({
  containerRef,
  enabled,
  onEscape,
}: UseGridCellNavigationOptions<T>): void {
  // Active cell coordinates [rowIndex, colIndex] across the full grid
  // (header rows first, then body rows).
  const activeRef = useRef<[number, number]>([0, 0]);
  const initializedRef = useRef(false);
  const firstBodyCellRef = useRef<HTMLTableCellElement | null>(null);
  const onEscapeRef = useRef(onEscape);
  useEffect(() => {
    onEscapeRef.current = onEscape;
  });

  const getGrid = useCallback(
    (): HTMLElement | null =>
      containerRef.current?.querySelector<HTMLElement>('[role="grid"]') ?? null,
    [containerRef]
  );

  const getMatrix = useCallback((): HTMLTableCellElement[][] => {
    const grid = getGrid();
    if (!grid) return [];
    return Array.from(grid.querySelectorAll<HTMLTableRowElement>("tr"), (row) =>
      Array.from(
        row.querySelectorAll<HTMLTableCellElement>(":scope > th, :scope > td")
      )
    );
  }, [getGrid]);

  // Index of the first body (data) row within the full matrix.
  const firstDataRow = useCallback((): number => {
    const grid = getGrid();
    const head = grid?.querySelector("thead");
    return head ? head.querySelectorAll<HTMLTableRowElement>("tr").length : 0;
  }, [getGrid]);

  const targetOf = useCallback((cell: HTMLTableCellElement): HTMLElement => {
    const widgets = cell.querySelectorAll<HTMLElement>(WIDGET_SELECTOR);
    return widgets.length === 1 ? widgets[0] : cell;
  }, []);

  // Make only the active cell's focus target tabbable.
  const applyRoving = useCallback(() => {
    const matrix = getMatrix();
    if (matrix.length === 0) return;
    const [r, c] = activeRef.current;
    const activeCell = matrix[r]?.[c];
    const activeTarget = activeCell ? targetOf(activeCell) : undefined;
    for (const row of matrix) {
      for (const cell of row) {
        const target = targetOf(cell);
        const isActive = target === activeTarget;
        target.tabIndex = isActive ? 0 : -1;
        // When the cell itself is the roving target (0 or 2+ widgets), suppress
        // all contained widgets in non-active cells so none escape as tab stops.
        if (target === cell && !isActive) {
          for (const widget of cell.querySelectorAll<HTMLElement>(
            WIDGET_SELECTOR
          )) {
            widget.tabIndex = -1;
          }
        }
      }
    }
  }, [getMatrix, targetOf]);

  const focusCell = useCallback(
    (r: number, c: number) => {
      const matrix = getMatrix();
      const row = matrix[r];
      if (!row || row.length === 0) return;
      const col = Math.min(Math.max(c, 0), row.length - 1);
      activeRef.current = [r, col];
      applyRoving();
      targetOf(row[col]).focus();
    },
    [getMatrix, applyRoving, targetOf]
  );

  // Keep the roving invariant when rows are recreated (pagination/sort/data
  // change). Skips the sweep when the DOM structure hasn't changed — arrow-key
  // navigation is already handled imperatively by focusCell/handleFocusIn.
  useLayoutEffect(() => {
    if (!enabled) return;
    const matrix = getMatrix();
    if (matrix.length === 0) return;

    const firstDataRowIdx = firstDataRow();
    const firstBodyCell = matrix[firstDataRowIdx]?.[0] ?? null;

    if (!initializedRef.current) {
      activeRef.current = [firstDataRowIdx, 0];
      initializedRef.current = true;
      firstBodyCellRef.current = firstBodyCell;
      applyRoving();
      return;
    }

    // Same DOM node → rows weren't recreated; skip the redundant sweep.
    if (firstBodyCell === firstBodyCellRef.current) return;

    // Different DOM node → rows were recreated. Clamp coordinates into the
    // new bounds and re-apply tabindex.
    firstBodyCellRef.current = firstBodyCell;
    let [r, c] = activeRef.current;
    if (!matrix[r]) r = Math.min(firstDataRowIdx, matrix.length - 1);
    c = Math.min(Math.max(c, 0), (matrix[r]?.length ?? 1) - 1);
    activeRef.current = [r, c];
    applyRoving();
  });

  useEffect(() => {
    if (!enabled) return;
    const container = containerRef.current;
    if (!container) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const grid = getGrid();
      if (!target || !grid?.contains(target)) return;

      if (e.key === "Escape") {
        onEscapeRef.current?.();
        return;
      }

      // Don't hijack typing inside an editable widget within a cell.
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement ||
        target.isContentEditable
      )
        return;

      const matrix = getMatrix();
      if (matrix.length === 0) return;
      const lastRow = matrix.length - 1;
      let [r, c] = activeRef.current;

      switch (e.key) {
        case "ArrowRight":
          c = Math.min(c + 1, (matrix[r]?.length ?? 1) - 1);
          break;
        case "ArrowLeft":
          c = Math.max(c - 1, 0);
          break;
        case "ArrowDown":
          r = Math.min(r + 1, lastRow);
          break;
        case "ArrowUp":
          r = Math.max(r - 1, firstDataRow());
          break;
        case "Home":
          if (e.ctrlKey || e.metaKey) r = firstDataRow();
          c = 0;
          break;
        case "End":
          if (e.ctrlKey || e.metaKey) r = lastRow;
          c = (matrix[r]?.length ?? 1) - 1;
          break;
        default:
          return;
      }

      e.preventDefault();
      focusCell(r, c);
    };

    // Mouse/Tab focus should sync the active coordinates so subsequent arrow
    // keys continue from wherever focus actually landed.
    const handleFocusIn = (e: FocusEvent) => {
      const target = e.target as HTMLElement | null;
      const grid = getGrid();
      const cell = target?.closest<HTMLTableCellElement>("th, td");
      if (!cell || !grid?.contains(cell)) return;
      const matrix = getMatrix();
      for (let r = 0; r < matrix.length; r++) {
        const c = matrix[r].indexOf(cell);
        if (c !== -1) {
          activeRef.current = [r, c];
          applyRoving();
          return;
        }
      }
    };

    container.addEventListener("keydown", handleKeyDown);
    container.addEventListener("focusin", handleFocusIn);
    return () => {
      container.removeEventListener("keydown", handleKeyDown);
      container.removeEventListener("focusin", handleFocusIn);
    };
  }, [
    enabled,
    containerRef,
    getGrid,
    getMatrix,
    firstDataRow,
    focusCell,
    applyRoving,
  ]);
}

export { useGridCellNavigation };
export type { UseGridCellNavigationOptions };
